// Derives per-entity before/after diffs from a trace's snapshot events.
//
// A trace emits snapshot events tagged with an entity, entityId and phase
// ('before' | 'after'). A diff lists only the properties whose value changed. A
// missing side means the entity did not exist then: a create (after only) lists
// every value as added; a delete (before + null after) lists every value as
// removed; an update lists just the changed columns.

function valuesEqual(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// Changed top-level properties between two snapshot payloads. A null payload
// (entity absent — e.g. a delete's 'after') is treated as no properties.
function diffData(before, after) {
  const changes = [];
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);

  for (const key of [...keys].sort()) {
    const from = before?.[key] ?? null;
    const to = after?.[key] ?? null;
    if (!valuesEqual(from, to)) changes.push({ key, before: from, after: to });
  }

  return changes;
}

export function buildDiffs(events) {
  const groups = new Map();

  for (const event of events) {
    if (event.eventType !== 'snapshot' || !event.entity) continue;

    const key = `${event.entity}:${event.entityId}`;
    let group = groups.get(key);
    if (!group) {
      group = { entity: event.entity, entityId: event.entityId, before: undefined, after: undefined };
      groups.set(key, group);
    }

    if (event.phase === 'before') group.before = event.eventData;
    else if (event.phase === 'after') group.after = event.eventData;
  }

  const diffs = [];
  for (const group of groups.values()) {
    // A missing (undefined) side means the entity did not exist then — treat it
    // as null so the change is captured.
    const before = group.before ?? null;
    const after = group.after ?? null;
    const kind = before === null ? 'created' : after === null ? 'deleted' : 'updated';

    diffs.push({
      entity: group.entity,
      entityId: group.entityId,
      kind,
      changes: diffData(before, after),
    });
  }

  return diffs;
}
